import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useTokenStore from "../store/store";
import useThemeStore from "../store/themeStore";
import { ThemeProps } from "../utils/theme";

function OAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setToken = useTokenStore((state) => state.setToken);
  const getThemes = useThemeStore((state) => state.theme as unknown as ThemeProps);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get("token");
    const userParam = searchParams.get("user");
    const errorParam = searchParams.get("error");

    if (errorParam) {
      let errorMessage = "Authentication failed. Please try again.";
      switch (errorParam) {
        case "auth_failed":
          errorMessage = "Authentication failed. Please try again.";
          break;
        case "email_required":
          errorMessage = "Email is required for registration. Please allow email access.";
          break;
        case "email_exists":
          const provider = searchParams.get("provider");
          errorMessage = provider && provider !== "local"
            ? `An account with this email already exists. Please sign in with ${provider}.`
            : "An account with this email already exists. Please sign in with your email and password.";
          break;
        case "account_deleted":
          errorMessage = "This account has been deleted.";
          break;
        case "invalid_state":
          errorMessage = "Invalid authentication state. Please try again.";
          break;
        case "server_error":
          errorMessage = "Server error. Please try again later.";
          break;
        default:
          errorMessage = "An error occurred during authentication.";
      }
      setError(errorMessage);
      return;
    }

    if (token && userParam) {
      try {
        const user = JSON.parse(decodeURIComponent(userParam));

        // Set token in store (same format as loginNew response)
        setToken({
          token,
          user: {
            id: user.id,
            fullname: user.fullname,
            email: user.email,
            userHandle: user.userHandle,
            accountStatus: user.accountStatus || "active",
            deletionScheduledFor: user.deletionScheduledFor,
          },
        });

        // Redirect to listing page
        navigate("/listing");
      } catch (e) {
        console.error("Failed to parse user data:", e);
        setError("Failed to process authentication data.");
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      }
    } else {
      setError("Missing authentication data.");
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    }
  }, [searchParams, setToken, navigate]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
        padding: "20px",
      }}
    >
      {error ? (
        <div
          style={{
            backgroundColor: "#fff",
            padding: "40px",
            borderRadius: "8px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            textAlign: "center",
            maxWidth: "400px",
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "50%",
              backgroundColor: "#fee2e2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}
          >
            <svg
              width="30"
              height="30"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#dc2626"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <h2 style={{ margin: "0 0 10px", color: "#333" }}>Authentication Error</h2>
          <p style={{ color: "#666", margin: "0 0 20px" }}>{error}</p>
          <button
            type="button"
            onClick={() => navigate("/login")}
            style={{
              padding: "10px 24px",
              borderRadius: "6px",
              border: "none",
              backgroundColor: getThemes?.color1 || "#1e1b4b",
              color: "#fff",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
            }}
          >
            Go to Login
          </button>
        </div>
      ) : (
        <div
          style={{
            backgroundColor: "#fff",
            padding: "40px",
            borderRadius: "8px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: "50px",
              height: "50px",
              border: `4px solid ${getThemes?.color1 || "#1e1b4b"}`,
              borderTopColor: "transparent",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 20px",
            }}
          />
          <style>
            {`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}
          </style>
          <p style={{ color: "#666", margin: 0 }}>Completing sign in...</p>
        </div>
      )}
    </div>
  );
}

export default OAuthCallback;
